import type { GetStaticPathsResult } from 'next';
import { ConfigProvider } from 'shared/config/ConfigContext';
import fhhData from 'shared/seo/data/fhh.json';
import { LinkTree } from 'shared/seo/LinkTree';
import { PopularCities } from 'shared/seo/PopularCities';
import { DestinationPage } from '@/components/DestinationPage';
import { cityLevel1Factory, vacationRentalSearchFactory } from '@/components/factory';
import { type OfferItem, Offers } from '@/components/offers/Offers';
import { config, feConfig } from '@/config';
import { fhhCities, fhhCountries, fhhMapping } from '@/data';
import { verticals } from '@/data/verticals';
import type { CityPageWithOffersProps, SeoPageContext } from '@/interfaces/pages';

const fhhOfferUrl = config.get('fhh.offers');
const baseApiUrl = `${fhhOfferUrl}?no_of_beds_from=2&mobile=false&nrFUSAds=0`;

export const getStaticPaths = async (): Promise<GetStaticPathsResult> => {
    const destinations = fhhCities.map((place) => ({
        params: { cityLevel1: place.cityPath, countryLevel1: place.countryPath },
    }));

    return {
        paths: destinations,
        fallback: false,
    };
};

export const getStaticProps = async (context: SeoPageContext) => {
    const destination = fhhCities.find(
        (place) =>
            place.cityPath === context.params.cityLevel1.toLowerCase() && place.countryPath === context.params.countryLevel1.toLowerCase(),
    );
    const destinationCountry = fhhCountries.find((place) => place.countryPath === context.params.countryLevel1.toLowerCase());

    const popularCities = fhhMapping[destination.id] ?? fhhMapping[destinationCountry.id] ?? [];

    const searchType = destination.country.toLowerCase() === 'norge' ? 'municipality' : 'locality';
    const offers: OfferItem[] = await fetch(encodeURI(`${baseApiUrl}&country=${destination.country}&${searchType}=${destination.city}`))
        .then((res) => (res.ok ? res.json() : Promise.reject(`Fetching offers failed (${res.status}): ${res.url}`)))
        .then((json) => json.resultItems ?? [])
        .catch((err) => {
            console.warn(err);
            return [];
        });

    return {
        props: {
            destination,
            popularCities,
            offers,
            config: feConfig,
        },
    };
};

const CityLevel1 = ({ destination, popularCities, offers, config }: CityPageWithOffersProps) => {
    const factory = cityLevel1Factory(destination, fhhData, vacationRentalSearchFactory());

    return (
        <ConfigProvider config={config}>
            <DestinationPage factory={factory} vertical={verticals.fhh} data={fhhData}>
                <Offers
                    destination={destination.city}
                    offers={offers}
                    vertical={verticals.fhh}
                    heading={fhhData.lang.headingOffers}
                    showMap
                />
                <PopularCities
                    destination={destination}
                    cities={popularCities}
                    cdnUrl={fhhData.cdnPath}
                    verticalUrl={fhhData.basePath}
                    lang={fhhData.lang}
                />
                <LinkTree
                    heading={fhhData.lang.linkTree.heading}
                    description={fhhData.lang.linkTree.description}
                    data={fhhData.linkTree}
                    url={fhhData.basePath}
                />
            </DestinationPage>
        </ConfigProvider>
    );
};

export default CityLevel1;
